import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { remove as removeDiacritics } from "diacritics";
import { FaSearch, FaCaretDown } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import API from "../../../api";
import AdminCustomerModal from "./AdminCustomerModal";
import "./admin-customers-list.css";
import "./../../../css/tables.css";

function AdminCustomerList() {
  const { id } = useParams();

  const newCustomer = {
    userType: "cust",
    userName: "",
    userFirstname: "",
    userEmail: "",
    userStatus: "active",
  };

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState({
    key: "userName",
    direction: "asc",
  });

  const [editedCustomer, setEditedCustomer] = useState(
    id === "new" ? { ...newCustomer } : null
  );

  // -----------------------------
  // API
  // -----------------------------

  useEffect(() => {
    API.get("/admin/customers").then((res) => {
      setCustomers(res.data || []);
    });
  }, []);

  // -----------------------------
  // TRI
  // -----------------------------

  const setOrderKey = useCallback((key) => {
    setOrder((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  }, []);

  // -----------------------------
  // FILTRAGE + TRI (memo)
  // -----------------------------

  const filteredCustomers = useMemo(() => {
    let data = [...customers];

    if (search.trim()) {
      const value = removeDiacritics(search.toLowerCase());

      data = data.filter((c) => {
        const comp = removeDiacritics(c.userCompName || "").toLowerCase();
        const name = removeDiacritics(c.userName || "").toLowerCase();
        const firstname = removeDiacritics(
          c.userFirstname || ""
        ).toLowerCase();
        const email = removeDiacritics(c.userEmail || "").toLowerCase();

        return (
          comp.includes(value) ||
          name.includes(value) ||
          firstname.includes(value) ||
          email.includes(value)
        );
      });
    }

    data.sort((a, b) => {
      const aVal = (a[order.key] || "").toString().toLowerCase();
      const bVal = (b[order.key] || "").toString().toLowerCase();

      return order.direction === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    return data;
  }, [customers, search, order]);

  // -----------------------------
  // MODAL
  // -----------------------------

  const handleCreateCustomer = () => {
    setEditedCustomer({ ...newCustomer });
  };

  const handleEditCustomer = (customer) => {
    setEditedCustomer(customer);
  };

  const handleCustomerCreated = (customer) => {
    setCustomers((prev) => [...prev, customer]);
  };

  const handleCustomerUpdated = (customer) => {
    setCustomers((prev) =>
      prev.map((c) => (c.userId === customer.userId ? customer : c))
    );
    setEditedCustomer(null);
  };

  const handleOnModalHide = () => {
    setEditedCustomer(null);
  };

  // -----------------------------
  // RENDER
  // -----------------------------

  return (
    <div id="CustomersList">
      <div className="container">
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text bg-green text-white">
                <FaSearch />
              </span>
              <input
                type="text"
                placeholder="Rechercher (nom, société, email)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="form-control"
              />
            </div>
          </div>
        </div>
        <div className="table-container">
          <table
            className={`table table-striped table-sm order-${order.direction}`}
          >
            <thead>
              <tr>
                <th
                  onClick={() => setOrderKey("userCompName")}
                  className="pointer"
                >
                  Société
                  {order.key === "userCompName" && (
                    <FaCaretDown className="carret" />
                  )}
                </th>

                <th
                  onClick={() => setOrderKey("userName")}
                  className="pointer"
                >
                  Nom
                  {order.key === "userName" && (
                    <FaCaretDown className="carret" />
                  )}
                </th>

                <th>Téléphone</th>
                <th>Email</th>
                <th>Ville</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.userId}>
                  <td className="text-center">
                    {customer.userCompName || "-"}
                  </td>

                  <td className="text-center">
                    {customer.userName} {customer.userFirstname}
                  </td>

                  <td className="text-center">
                    {customer.userPhone || "-"}
                  </td>

                  <td className="text-center">
                    {customer.userEmail}
                  </td>

                  <td className="text-center">
                    {customer.userCity || "-"}
                  </td>

                  <td className="text-center">
                    <span
                      className="text-green pointer"
                      onClick={() => handleEditCustomer(customer)}
                      title="Paramètres"
                    >
                      <MdEdit size={18} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editedCustomer && (
        <AdminCustomerModal
          show={true}
          onHide={handleOnModalHide}
          customer={editedCustomer}
          onCreated={handleCustomerCreated}
          onUpdated={handleCustomerUpdated}
        />
      )}
    </div>
  );
}

export default AdminCustomerList;